import React from 'react';
import { Helmet } from 'react-helmet-async';
import seo from './pages.json';

// Per-page <head> tags. Public pages pass their `path` and get the title, description, canonical URL and Open
// Graph / Twitter tags from pages.json (the same file the build script uses to pre-render these tags and write the
// sitemap). The tags in public/index.html carry data-rh, so these replace them instead of duplicating them.
export const pageMeta = (path) => seo.pages.find((p) => p.path === path);

const absolute = (path) => `${seo.siteUrl}${path === '/' ? '/' : path}`;

const Seo = ({ path, title, description, noindex = false, jsonLd }) => {
  const meta = path ? pageMeta(path) : null;
  const pageTitle = title ?? meta?.title ?? seo.siteName;
  const pageDescription = description ?? meta?.description;
  const url = path ? absolute(path) : null;
  const image = `${seo.siteUrl}${seo.image}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      {pageDescription && <meta name="description" content={pageDescription} />}
      {noindex && <meta name="robots" content="noindex" />}
      {url && <link rel="canonical" href={url} />}
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:title" content={pageTitle} />
      {pageDescription && <meta property="og:description" content={pageDescription} />}
      <meta property="og:type" content="website" />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={pageTitle} />
      {pageDescription && <meta name="twitter:description" content={pageDescription} />}
      <meta name="twitter:image" content={image} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default Seo;

// schema.org EducationalOrganization built from School Settings (only fields that are set).
export const organizationJsonLd = (settings) => {
  const sameAs = [settings.facebook_url, settings.twitter_url, settings.instagram_url].filter(Boolean);
  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: settings.school_name,
    url: settings.website_url || seo.siteUrl,
    logo: `${seo.siteUrl}${seo.image}`,
    ...(settings.address ? { address: { '@type': 'PostalAddress', streetAddress: settings.address, addressCountry: 'PK' } } : {}),
    ...(settings.phones?.[0] ? { telephone: settings.phones[0].tel } : {}),
    ...(settings.email_general ? { email: settings.email_general } : {}),
    ...(sameAs.length ? { sameAs } : {})
  };
};

// schema.org FAQPage from the approved FAQ entries.
export const faqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer }
  }))
});
